import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Box, keyframes } from '@mui/material';
import type { WaveformProps } from '../DiscordAudioPlayer.types';
import { getWaveformContainerStyles } from '../DiscordAudioPlayer.styles';
import { WAVEFORM_BAR_WIDTH, WAVEFORM_BAR_GAP, COLORS } from '../utils/constants';

const pulseAnimation = keyframes`
  0%   { transform: scaleY(1); }
  50%  { transform: scaleY(1.3); }
  100% { transform: scaleY(1); }
`;

const getPercentageFromEvent = (
  event: PointerEvent | React.PointerEvent,
  element: HTMLElement,
): number => {
  const rect = element.getBoundingClientRect();
  return Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
};

const getActiveBarIndex = (progress: number, totalBars: number): number => {
  if (progress <= 0) return -1;
  if (progress >= 100) return totalBars - 1;
  return Math.floor((progress / 100) * totalBars);
};

/** Extended WaveformProps with optional bufferProgress (0-100) */
export interface ExtendedWaveformProps extends WaveformProps {
  /**
   * How much audio is buffered/ready, expressed as a percentage (0-100).
   * Bars up to this position render in bufferedColor instead of unplayedColor.
   * Leave undefined to use the original two-color behaviour.
   */
  bufferProgress?: number;
  /** Color for buffered-but-not-yet-played bars */
  bufferedColor?: string;
}

export const Waveform: React.FC<ExtendedWaveformProps> = ({
  data,
  progress,
  bufferProgress,
  barWidth = WAVEFORM_BAR_WIDTH,
  barGap = WAVEFORM_BAR_GAP,
  playedColor = COLORS.waveformPlayed,
  bufferedColor = 'rgba(255, 255, 255, 0.55)',
  unplayedColor = COLORS.waveformUnplayed,
  clickable = true,
  onSeek,
  onScrubStart,
  onScrubEnd,
  sx,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [animatingBarIndex, setAnimatingBarIndex] = useState<number | null>(null);
  const prevActiveBarRef = useRef<number>(-1);

  useEffect(() => {
    const currentActiveBar = getActiveBarIndex(progress, data.length);
    if (currentActiveBar > prevActiveBarRef.current && !isDragging) {
      setAnimatingBarIndex(currentActiveBar);
      const id = setTimeout(() => setAnimatingBarIndex(null), 300);
      prevActiveBarRef.current = currentActiveBar;
      return () => clearTimeout(id);
    }
    if (currentActiveBar !== prevActiveBarRef.current) {
      prevActiveBarRef.current = currentActiveBar;
    }
  }, [progress, data.length, isDragging]);

  const handleSeek = useCallback(
    (percentage: number) => {
      if (clickable && onSeek) onSeek(percentage);
    },
    [clickable, onSeek],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!clickable || !onSeek || !containerRef.current) return;
      containerRef.current.setPointerCapture(event.pointerId);
      setIsDragging(true);
      onScrubStart?.();
      handleSeek(getPercentageFromEvent(event, containerRef.current));
    },
    [clickable, onSeek, handleSeek, onScrubStart],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging || !containerRef.current) return;
      handleSeek(getPercentageFromEvent(event, containerRef.current));
    },
    [isDragging, handleSeek],
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging) return;
      containerRef.current?.releasePointerCapture(event.pointerId);
      setIsDragging(false);
      onScrubEnd?.();
    },
    [isDragging, onScrubEnd],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!clickable || !onSeek) return;
      const step = event.shiftKey ? 10 : 1;
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          onSeek(Math.max(0, progress - step));
          break;
        case 'ArrowRight':
          event.preventDefault();
          onSeek(Math.min(100, progress + step));
          break;
        case 'Home':
          event.preventDefault();
          onSeek(0);
          break;
        case 'End':
          event.preventDefault();
          onSeek(100);
          break;
      }
    },
    [clickable, onSeek, progress],
  );

  return (
    <Box
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
      role={clickable ? 'slider' : 'presentation'}
      aria-label={clickable ? 'Seek position' : undefined}
      aria-valuemin={clickable ? 0 : undefined}
      aria-valuemax={clickable ? 100 : undefined}
      aria-valuenow={clickable ? Math.round(progress) : undefined}
      tabIndex={clickable ? 0 : -1}
      sx={[
        getWaveformContainerStyles(clickable, barGap),
        { touchAction: 'none', userSelect: 'none' },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {data.map((height, index) => {
        const barProgress = (index / data.length) * 100;
        const isPlayed = barProgress < progress;
        const isBuffered =
          bufferProgress !== undefined && barProgress < bufferProgress && !isPlayed;
        const isAnimating = index === animatingBarIndex;

        const bgColor = isPlayed ? playedColor : isBuffered ? bufferedColor : unplayedColor;

        return (
          <Box
            key={index}
            sx={{
              backgroundColor: bgColor,
              borderRadius: '2px',
              height: `${height * 100}%`,
              minHeight: 4,
              pointerEvents: 'none',
              transition: isDragging ? 'none' : 'background-color 0.15s ease',
              width: barWidth,
              transformOrigin: 'center center',
              animation: isAnimating ? `${pulseAnimation} 300ms ease-out` : 'none',
            }}
          />
        );
      })}
    </Box>
  );
};

export default Waveform;
